import type { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { DESKTOP_BREAKPOINT } from '../../../../styles/breakpoints';
import { maxOnboardingContentWidth, onboardingFooterScrollReserve, space } from '../../../../styles/onboardingUiTokens';
import { OnboardingSupportHint } from './OnboardingSupportHint';

export type OnboardingBodyProps = Omit<HTMLAttributes<HTMLElement>, 'children'> & {
  children: ReactNode;
  /** When true, horizontal padding is omitted (rare full-bleed bodies). */
  disableHorizontalPadding?: boolean;
  /** When true, the default support contact hint at the bottom of the body is omitted. */
  disableSupportHint?: boolean;
  /** Rendered after the support hint — for trailing legal/footnote copy the design places below it. */
  afterSupportHint?: ReactNode;
};

const ScrollRegion = styled.main`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: ${onboardingFooterScrollReserve};
  margin-bottom: ${space.md};

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    flex: none;
    padding-bottom: 0;
  }
`;

const Inner = styled.div<{ $noHorizontalPad: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: ${maxOnboardingContentWidth};
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding-top: ${space.lg};
  padding-left: ${({ $noHorizontalPad }) => ($noHorizontalPad ? 0 : space.lg)};
  padding-right: ${({ $noHorizontalPad }) => ($noHorizontalPad ? 0 : space.lg)};
`;

/**
 * Scrollable main column for onboarding content. Appends a support contact hint after `children`
 * unless `disableSupportHint` is set. Pads the bottom so fixed footer chrome does not cover the
 * last lines of content.
 */
export function OnboardingBody({
  children,
  disableHorizontalPadding = false,
  disableSupportHint = false,
  afterSupportHint,
  className,
  ...rest
}: OnboardingBodyProps) {
  return (
    <ScrollRegion className={className} {...rest}>
      <Inner $noHorizontalPad={disableHorizontalPadding}>
        {children}
        {disableSupportHint ? null : <OnboardingSupportHint variant="compact" />}
        {afterSupportHint}
      </Inner>
    </ScrollRegion>
  );
}
